/**
 * Application Constants
 * Centralized constants for workflow statuses, file types, user roles, etc.
 */

// Workflow Statuses (24 total)
export const WORKFLOW_STATUS = {
  // Sales Phase
  PENDING_PROJECT_NUMBER: 'pending_project_number',
  ACTIVE_PROJECT: 'active_project',

  // Measurement & 2D Phase
  MEASUREMENT_PENDING: 'measurement_pending',
  MEASUREMENT_IN_PROGRESS: 'measurement_in_progress',
  MEASUREMENT_DONE: 'measurement_done',
  TWO_D_PENDING: '2d_pending',
  TWO_D_IN_PROGRESS: '2d_in_progress',
  TWO_D_PENDING_CLIENT_REVIEW: '2d_pending_client_review',
  TWO_D_AMENDMENT_REQUIRED: '2d_amendment_required',
  TWO_D_APPROVED: '2d_approved',

  // 3D Phase
  THREE_D_PENDING: '3d_pending',
  THREE_D_IN_PROGRESS: '3d_in_progress',
  THREE_D_PENDING_CLIENT_REVIEW: '3d_pending_client_review',
  THREE_D_AMENDMENT_REQUIRED: '3d_amendment_required',
  THREE_D_APPROVED: '3d_approved',

  // DD & Quotation Phase
  DD_PENDING: 'dd_pending',
  DD_IN_PROGRESS: 'dd_in_progress',
  DD_SUBMITTED_TO_QUOTATION: 'dd_submitted_to_quotation',
  QUOTATION_IN_PROGRESS: 'quotation_in_progress',

  // Contract Phase
  READY_FOR_SIGNING: 'ready_for_signing',
  SIGNING_SCHEDULED: 'signing_scheduled',
  SIGNING_AMENDMENT_REQUIRED: 'signing_amendment_required',

  // Terminal States
  PROJECT_CLOSED_WON: 'project_closed_won',
  PROJECT_CLOSED_LOST: 'project_closed_lost',
  PROJECT_CANCELLED: 'project_cancelled'
};

// Workflow Status Labels (Human Readable)
export const WORKFLOW_STATUS_LABELS = {
  [WORKFLOW_STATUS.PENDING_PROJECT_NUMBER]: 'Pending Project Number',
  [WORKFLOW_STATUS.ACTIVE_PROJECT]: 'Active Project',
  [WORKFLOW_STATUS.MEASUREMENT_PENDING]: 'Measurement Pending',
  [WORKFLOW_STATUS.MEASUREMENT_IN_PROGRESS]: 'Measurement In Progress',
  [WORKFLOW_STATUS.MEASUREMENT_DONE]: 'Measurement Done',
  [WORKFLOW_STATUS.TWO_D_PENDING]: '2D Drawing Pending',
  [WORKFLOW_STATUS.TWO_D_IN_PROGRESS]: '2D Drawing In Progress',
  [WORKFLOW_STATUS.TWO_D_PENDING_CLIENT_REVIEW]: '2D Pending Client Review',
  [WORKFLOW_STATUS.TWO_D_AMENDMENT_REQUIRED]: '2D Amendment Required',
  [WORKFLOW_STATUS.TWO_D_APPROVED]: '2D Approved',
  [WORKFLOW_STATUS.THREE_D_PENDING]: '3D Pending',
  [WORKFLOW_STATUS.THREE_D_IN_PROGRESS]: '3D In Progress',
  [WORKFLOW_STATUS.THREE_D_PENDING_CLIENT_REVIEW]: '3D Pending Client Review',
  [WORKFLOW_STATUS.THREE_D_AMENDMENT_REQUIRED]: '3D Amendment Required',
  [WORKFLOW_STATUS.THREE_D_APPROVED]: '3D Approved',
  [WORKFLOW_STATUS.DD_PENDING]: 'DD Pending',
  [WORKFLOW_STATUS.DD_IN_PROGRESS]: 'DD In Progress',
  [WORKFLOW_STATUS.DD_SUBMITTED_TO_QUOTATION]: 'DD Submitted to Quotation',
  [WORKFLOW_STATUS.QUOTATION_IN_PROGRESS]: 'Quotation In Progress',
  [WORKFLOW_STATUS.READY_FOR_SIGNING]: 'Ready for Signing',
  [WORKFLOW_STATUS.SIGNING_SCHEDULED]: 'Signing Scheduled',
  [WORKFLOW_STATUS.SIGNING_AMENDMENT_REQUIRED]: 'Signing Amendment Required',
  [WORKFLOW_STATUS.PROJECT_CLOSED_WON]: 'Project Won ✅',
  [WORKFLOW_STATUS.PROJECT_CLOSED_LOST]: 'Project Lost ❌',
  [WORKFLOW_STATUS.PROJECT_CANCELLED]: 'Cancelled'
};

// Status Colors for Badges
export const STATUS_COLORS = {
  [WORKFLOW_STATUS.PENDING_PROJECT_NUMBER]: 'gray',
  [WORKFLOW_STATUS.ACTIVE_PROJECT]: 'blue',
  [WORKFLOW_STATUS.MEASUREMENT_PENDING]: 'yellow',
  [WORKFLOW_STATUS.MEASUREMENT_IN_PROGRESS]: 'blue',
  [WORKFLOW_STATUS.MEASUREMENT_DONE]: 'green',
  [WORKFLOW_STATUS.TWO_D_PENDING]: 'yellow',
  [WORKFLOW_STATUS.TWO_D_IN_PROGRESS]: 'blue',
  [WORKFLOW_STATUS.TWO_D_PENDING_CLIENT_REVIEW]: 'purple',
  [WORKFLOW_STATUS.TWO_D_AMENDMENT_REQUIRED]: 'orange',
  [WORKFLOW_STATUS.TWO_D_APPROVED]: 'green',
  [WORKFLOW_STATUS.THREE_D_PENDING]: 'yellow',
  [WORKFLOW_STATUS.THREE_D_IN_PROGRESS]: 'blue',
  [WORKFLOW_STATUS.THREE_D_PENDING_CLIENT_REVIEW]: 'purple',
  [WORKFLOW_STATUS.THREE_D_AMENDMENT_REQUIRED]: 'orange',
  [WORKFLOW_STATUS.THREE_D_APPROVED]: 'green',
  [WORKFLOW_STATUS.DD_PENDING]: 'yellow',
  [WORKFLOW_STATUS.DD_IN_PROGRESS]: 'blue',
  [WORKFLOW_STATUS.DD_SUBMITTED_TO_QUOTATION]: 'purple',
  [WORKFLOW_STATUS.QUOTATION_IN_PROGRESS]: 'blue',
  [WORKFLOW_STATUS.READY_FOR_SIGNING]: 'green',
  [WORKFLOW_STATUS.SIGNING_SCHEDULED]: 'purple',
  [WORKFLOW_STATUS.SIGNING_AMENDMENT_REQUIRED]: 'orange',
  [WORKFLOW_STATUS.PROJECT_CLOSED_WON]: 'green',
  [WORKFLOW_STATUS.PROJECT_CLOSED_LOST]: 'red',
  [WORKFLOW_STATUS.PROJECT_CANCELLED]: 'gray'
};

// Project Phases (must match backend enum values)
export const PROJECT_PHASE = {
  SALES: 'sales',
  MEASUREMENT: 'measurement',
  TWO_D: '2d',
  THREE_D: '3d',
  DD: 'dd',
  QUOTATION: 'quotation',
  CONTRACT: 'contract',
  CLOSED: 'closed'
};

// Phase Labels
export const PHASE_LABELS = {
  [PROJECT_PHASE.SALES]: 'Sales & Booking',
  [PROJECT_PHASE.MEASUREMENT]: 'Measurement',
  [PROJECT_PHASE.TWO_D]: '2D Drawing',
  [PROJECT_PHASE.THREE_D]: '3D Visualization',
  [PROJECT_PHASE.DD]: 'DD Development',
  [PROJECT_PHASE.QUOTATION]: 'Quotation',
  [PROJECT_PHASE.CONTRACT]: 'Contract Signing',
  [PROJECT_PHASE.CLOSED]: 'Closed'
};

// Task Types (must match backend enum values)
export const TASK_TYPE = {
  SITE_MEASUREMENT: 'site_measurement',
  TWO_D_DRAWING: '2d_drawing',
  THREE_D_VISUALIZATION: '3d_visualization',
  DD_DEVELOPMENT: 'dd_development',
  QUOTATION_CALCULATION: 'quotation_calculation'
};

// Task Type Labels
export const TASK_TYPE_LABELS = {
  [TASK_TYPE.SITE_MEASUREMENT]: 'Site Measurement',
  [TASK_TYPE.TWO_D_DRAWING]: '2D Drawing',
  [TASK_TYPE.THREE_D_VISUALIZATION]: '3D Visualization',
  [TASK_TYPE.DD_DEVELOPMENT]: 'DD Development',
  [TASK_TYPE.QUOTATION_CALCULATION]: 'Quotation Calculation'
};

// Task Status
export const TASK_STATUS = {
  PENDING_ASSIGNMENT: 'pending_assignment',
  ASSIGNED: 'assigned',
  IN_PROGRESS: 'in_progress',
  SUBMITTED: 'submitted',
  APPROVED: 'approved',
  AMENDMENT_REQUIRED: 'amendment_required'
};

// Task Status Labels
export const TASK_STATUS_LABELS = {
  [TASK_STATUS.PENDING_ASSIGNMENT]: 'Pending Assignment',
  [TASK_STATUS.ASSIGNED]: 'Assigned',
  [TASK_STATUS.IN_PROGRESS]: 'In Progress',
  [TASK_STATUS.SUBMITTED]: 'Submitted',
  [TASK_STATUS.APPROVED]: 'Approved',
  [TASK_STATUS.AMENDMENT_REQUIRED]: 'Amendment Required'
};

// File Types
export const FILE_TYPE = {
  SITE_PHOTOS: 'site_photos',
  SITE_MEASUREMENTS: 'site_measurements',
  TWO_D_DRAWING: '2d_drawing',
  THREE_D_RENDER: '3d_render',
  DD_SPECS: 'dd_specs',
  BQ_NO_COST: 'bq_no_cost',
  BQ_WITH_COST: 'bq_with_cost',
  QUOTATION: 'quotation',
  CONTRACT: 'contract',
  DEPOSIT_RECEIPT: 'deposit_receipt',
  LOCK_PRICE_RECEIPT: 'lock_price_receipt',
  PAYMENT_RECEIPT: 'payment_receipt',
  VO_QUOTATION: 'vo_quotation',
  VO_INVOICE: 'vo_invoice',
  OTHER: 'other'
};

// File Type Labels
export const FILE_TYPE_LABELS = {
  [FILE_TYPE.SITE_PHOTOS]: 'Site Photos',
  [FILE_TYPE.SITE_MEASUREMENTS]: 'Site Measurements',
  [FILE_TYPE.TWO_D_DRAWING]: '2D Drawing',
  [FILE_TYPE.THREE_D_RENDER]: '3D Render',
  [FILE_TYPE.DD_SPECS]: 'DD Specifications',
  [FILE_TYPE.BQ_NO_COST]: 'BQ (Without Cost)',
  [FILE_TYPE.BQ_WITH_COST]: 'BQ (With Cost)',
  [FILE_TYPE.QUOTATION]: 'Quotation',
  [FILE_TYPE.CONTRACT]: 'Contract',
  [FILE_TYPE.DEPOSIT_RECEIPT]: 'Deposit Receipt',
  [FILE_TYPE.LOCK_PRICE_RECEIPT]: 'Lock Price Receipt',
  [FILE_TYPE.PAYMENT_RECEIPT]: 'Payment Receipt',
  [FILE_TYPE.VO_QUOTATION]: 'VO Quotation',
  [FILE_TYPE.VO_INVOICE]: 'VO Invoice',
  [FILE_TYPE.OTHER]: 'Other'
};

// Amendment Cycle Helper
// Returns human-readable label for amendment cycle number
// null/undefined = Legacy, 0 = Initial, 1+ = Amendment N
export const getAmendmentCycleLabel = (cycle) => {
  if (cycle === null || cycle === undefined) return 'Legacy';
  if (cycle === 0) return 'Initial';
  return `Amendment ${cycle}`;
};

// Short version for badges (e.g., "A1" instead of "Amendment 1")
export const getAmendmentCycleShortLabel = (cycle) => {
  if (cycle === null || cycle === undefined) return null;
  if (cycle === 0) return 'Initial';
  return `A${cycle}`;
};

// User Roles
export const USER_ROLE = {
  MANAGER: 'manager',
  AGENT: 'agent',
  SID: 'sid',
  ASID: 'asid',
  DESIGNER: 'designer',
  DRAUGHTER: 'draughter',
  QUOTATION_TEAM: 'quotation_team'
};

// Role Labels
export const ROLE_LABELS = {
  [USER_ROLE.MANAGER]: 'Manager',
  [USER_ROLE.AGENT]: 'Agent',
  [USER_ROLE.SID]: 'Senior Interior Designer',
  [USER_ROLE.ASID]: 'Assistant SID',
  [USER_ROLE.DESIGNER]: '3D Designer',
  [USER_ROLE.DRAUGHTER]: 'Draughtsperson',
  [USER_ROLE.QUOTATION_TEAM]: 'Quotation Team'
};

// Meeting Types
export const MEETING_TYPE = {
  TWO_D_REVIEW: '2d_review',
  THREE_D_REVIEW: '3d_review',
  FINAL_REVIEW: 'final_review',
  IN_PERSON: 'in_person',
  VIRTUAL: 'virtual',
  DOCUMENT_DELIVERY: 'document_delivery'
};

// Meeting Outcomes
export const MEETING_OUTCOME = {
  APPROVED: 'approved',
  AMENDMENTS_REQUIRED: 'amendments_required',
  REJECTED: 'rejected',
  SIGNED: 'signed',
  NEGOTIATION_NEEDED: 'negotiation_needed',
  CLIENT_DECLINED: 'client_declined',
  PENDING_REVIEW: 'pending_review'
};

// Blocker Types
export const BLOCKER_TYPE = {
  PRICE: 'price',
  TIMELINE: 'timeline',
  SCOPE: 'scope',
  MATERIALS: 'materials',
  PAYMENT_TERMS: 'payment_terms',
  WARRANTY: 'warranty',
  LEGAL_TERMS: 'legal_terms',
  OTHER: 'other'
};

// Blocker Type Labels
export const BLOCKER_TYPE_LABELS = {
  [BLOCKER_TYPE.PRICE]: 'Price Negotiation',
  [BLOCKER_TYPE.TIMELINE]: 'Timeline Concerns',
  [BLOCKER_TYPE.SCOPE]: 'Scope Changes',
  [BLOCKER_TYPE.MATERIALS]: 'Material Selection',
  [BLOCKER_TYPE.PAYMENT_TERMS]: 'Payment Terms',
  [BLOCKER_TYPE.WARRANTY]: 'Warranty Terms',
  [BLOCKER_TYPE.LEGAL_TERMS]: 'Legal Terms',
  [BLOCKER_TYPE.OTHER]: 'Other'
};

// Quotation Item Status
export const QUOTATION_ITEM_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  ALTERNATIVE_NEEDED: 'alternative_needed'
};

// Quotation Categories
export const QUOTATION_CATEGORY = {
  FURNITURE: 'furniture',
  LIGHTING: 'lighting',
  FLOORING: 'flooring',
  WALL_FINISHES: 'wall_finishes',
  CEILING: 'ceiling',
  KITCHEN: 'kitchen',
  BATHROOM: 'bathroom',
  CARPENTRY: 'carpentry',
  ELECTRICAL: 'electrical',
  PLUMBING: 'plumbing',
  HVAC: 'hvac',
  OTHER: 'other'
};

// API Endpoints (base paths)
export const API_ENDPOINTS = {
  AUTH: '/auth',
  USERS: '/users',
  LEADS: '/leads',
  SALES_FORMS: '/sales-forms',
  DESIGN_PROJECTS: '/design-projects',
  PROJECT_FILES: '/project-files',
  QUOTATIONS: '/quotations',
  CONTRACTS: '/contracts',
  SIGNING_APPOINTMENTS: '/signing-appointments'
};

// Date Format
export const DATE_FORMAT = {
  DISPLAY: 'MMM DD, YYYY',
  DISPLAY_WITH_TIME: 'MMM DD, YYYY hh:mm A',
  INPUT: 'YYYY-MM-DD',
  API: 'YYYY-MM-DDTHH:mm:ss.SSSZ'
};

// Currency
export const CURRENCY = {
  SYMBOL: 'RM',
  CODE: 'MYR',
  LOCALE: 'en-MY'
};

// File Size Limits (in bytes)
export const FILE_SIZE_LIMITS = {
  [FILE_TYPE.SITE_PHOTOS]: 10 * 1024 * 1024, // 10 MB
  [FILE_TYPE.SITE_MEASUREMENTS]: 20 * 1024 * 1024, // 20 MB
  [FILE_TYPE.TWO_D_DRAWING]: 50 * 1024 * 1024, // 50 MB
  [FILE_TYPE.THREE_D_RENDER]: 200 * 1024 * 1024, // 200 MB
  [FILE_TYPE.DD_SPECS]: 50 * 1024 * 1024, // 50 MB
  [FILE_TYPE.QUOTATION]: 10 * 1024 * 1024, // 10 MB
  [FILE_TYPE.CONTRACT]: 10 * 1024 * 1024, // 10 MB
  [FILE_TYPE.LOCK_PRICE_RECEIPT]: 10 * 1024 * 1024, // 10 MB
  [FILE_TYPE.PAYMENT_RECEIPT]: 10 * 1024 * 1024, // 10 MB
  [FILE_TYPE.VO_QUOTATION]: 10 * 1024 * 1024, // 10 MB
  [FILE_TYPE.VO_INVOICE]: 10 * 1024 * 1024, // 10 MB
  [FILE_TYPE.OTHER]: 20 * 1024 * 1024 // 20 MB
};

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100
};

// Notification Types
export const NOTIFICATION_TYPE = {
  TASK_ASSIGNED: 'task_assigned',
  TASK_SUBMITTED: 'task_submitted',
  DESIGN_APPROVED: 'design_approved',
  AMENDMENT_REQUESTED: 'amendment_requested',
  QUOTATION_FINALIZED: 'quotation_finalized',
  CONTRACT_GENERATED: 'contract_generated',
  SIGNING_SCHEDULED: 'signing_scheduled',
  PROJECT_STATUS_CHANGED: 'project_status_changed',
  FILE_UPLOADED: 'file_uploaded',
  PROJECT_WON: 'project_won',
  PROJECT_LOST: 'project_lost'
};

// Aliases for backwards compatibility
export const PROJECT_PHASE_LABELS = PHASE_LABELS;

// ========================================
// Site Database Constants
// ========================================

// Site Status (pending_ss_assignment -> active -> on_hold/completed)
export const SITE_STATUS = {
  PENDING_SS_ASSIGNMENT: 'pending_ss_assignment',
  ACTIVE: 'active',
  ON_HOLD: 'on_hold',
  COMPLETED: 'completed'
};

export const SITE_STATUS_LABELS = {
  [SITE_STATUS.PENDING_SS_ASSIGNMENT]: 'Pending SS Assignment',
  [SITE_STATUS.ACTIVE]: 'Active',
  [SITE_STATUS.ON_HOLD]: 'On Hold',
  [SITE_STATUS.COMPLETED]: 'Completed'
};

export const SITE_STATUS_COLORS = {
  [SITE_STATUS.PENDING_SS_ASSIGNMENT]: 'yellow',
  [SITE_STATUS.ACTIVE]: 'blue',
  [SITE_STATUS.ON_HOLD]: 'gray',
  [SITE_STATUS.COMPLETED]: 'green'
};

// Site Phase Status
export const SITE_PHASE_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  SKIPPED: 'skipped'
};

export const SITE_PHASE_STATUS_LABELS = {
  [SITE_PHASE_STATUS.PENDING]: 'Pending',
  [SITE_PHASE_STATUS.IN_PROGRESS]: 'In Progress',
  [SITE_PHASE_STATUS.COMPLETED]: 'Completed',
  [SITE_PHASE_STATUS.SKIPPED]: 'Skipped'
};

export const SITE_PHASE_STATUS_COLORS = {
  [SITE_PHASE_STATUS.PENDING]: 'gray',
  [SITE_PHASE_STATUS.IN_PROGRESS]: 'blue',
  [SITE_PHASE_STATUS.COMPLETED]: 'green',
  [SITE_PHASE_STATUS.SKIPPED]: 'yellow'
};

// Site Task Status
export const SITE_TASK_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed'
};

export const SITE_TASK_STATUS_LABELS = {
  [SITE_TASK_STATUS.PENDING]: 'Pending',
  [SITE_TASK_STATUS.IN_PROGRESS]: 'In Progress',
  [SITE_TASK_STATUS.COMPLETED]: 'Completed'
};

// Site Project Type
export const SITE_PROJECT_TYPE = {
  RENOVATION: 'renovation',
  BUILD_FROM_LAND: 'build_from_land'
};

export const SITE_PROJECT_TYPE_LABELS = {
  [SITE_PROJECT_TYPE.RENOVATION]: 'Renovation',
  [SITE_PROJECT_TYPE.BUILD_FROM_LAND]: 'Build from Land'
};

// Site Phase Keys (for reference)
export const SITE_PHASE_KEYS = {
  // Renovation phases
  HANDOVER: 'handover',
  DEMOLITION: 'demolition',
  ME_1ST_FIX: 'me_1st_fix',
  WET_WORKS: 'wet_works',
  CEILING_PARTITION: 'ceiling_partition',
  CARPENTRY: 'carpentry',
  PAINTING: 'painting',
  ME_2ND_FIX: 'me_2nd_fix',
  DEFECTS_QC: 'defects_qc',
  CLIENT_HANDOVER: 'client_handover',
  // Build from land phases
  SITE_HANDOVER: 'site_handover',
  FOUNDATION: 'foundation',
  STRUCTURE: 'structure',
  ROOFING: 'roofing',
  EXTERNAL_WALLS: 'external_walls',
  WINDOWS_DOORS: 'windows_doors',
  INTERNAL_WALLS: 'internal_walls',
  CEILING: 'ceiling',
  EXTERNAL_WORKS: 'external_works'
};

// Site History Change Types
export const SITE_HISTORY_CHANGE_TYPE = {
  SITE_CREATED: 'site_created',
  STATUS_CHANGED: 'status_changed',
  PHASE_STARTED: 'phase_started',
  PHASE_COMPLETED: 'phase_completed',
  PHASE_SKIPPED: 'phase_skipped',
  PHASE_ADDED: 'phase_added',
  PHASE_REMOVED: 'phase_removed',
  PHASE_REORDERED: 'phase_reordered',
  PHASE_WEIGHT_UPDATED: 'phase_weight_updated',
  PHASE_PROGRESS_UPDATED: 'phase_progress_updated',
  TASK_CREATED: 'task_created',
  TASK_COMPLETED: 'task_completed',
  TASK_DELETED: 'task_deleted',
  NOTES_UPDATED: 'notes_updated',
  ATTENDANCE_CHECK_IN: 'attendance_check_in',
  ATTENDANCE_CHECK_OUT: 'attendance_check_out'
};

export const SITE_HISTORY_CHANGE_TYPE_LABELS = {
  [SITE_HISTORY_CHANGE_TYPE.SITE_CREATED]: 'Site Created',
  [SITE_HISTORY_CHANGE_TYPE.STATUS_CHANGED]: 'Status Changed',
  [SITE_HISTORY_CHANGE_TYPE.PHASE_STARTED]: 'Phase Started',
  [SITE_HISTORY_CHANGE_TYPE.PHASE_COMPLETED]: 'Phase Completed',
  [SITE_HISTORY_CHANGE_TYPE.PHASE_SKIPPED]: 'Phase Skipped',
  [SITE_HISTORY_CHANGE_TYPE.PHASE_ADDED]: 'Phase Added',
  [SITE_HISTORY_CHANGE_TYPE.PHASE_REMOVED]: 'Phase Removed',
  [SITE_HISTORY_CHANGE_TYPE.PHASE_REORDERED]: 'Phases Reordered',
  [SITE_HISTORY_CHANGE_TYPE.PHASE_WEIGHT_UPDATED]: 'Phase Weight Updated',
  [SITE_HISTORY_CHANGE_TYPE.PHASE_PROGRESS_UPDATED]: 'Progress Updated',
  [SITE_HISTORY_CHANGE_TYPE.TASK_CREATED]: 'Task Created',
  [SITE_HISTORY_CHANGE_TYPE.TASK_COMPLETED]: 'Task Completed',
  [SITE_HISTORY_CHANGE_TYPE.TASK_DELETED]: 'Task Deleted',
  [SITE_HISTORY_CHANGE_TYPE.NOTES_UPDATED]: 'Notes Updated',
  [SITE_HISTORY_CHANGE_TYPE.ATTENDANCE_CHECK_IN]: 'Checked In',
  [SITE_HISTORY_CHANGE_TYPE.ATTENDANCE_CHECK_OUT]: 'Checked Out'
};

export default {
  WORKFLOW_STATUS,
  WORKFLOW_STATUS_LABELS,
  STATUS_COLORS,
  PROJECT_PHASE,
  PHASE_LABELS,
  TASK_TYPE,
  TASK_TYPE_LABELS,
  TASK_STATUS,
  TASK_STATUS_LABELS,
  FILE_TYPE,
  FILE_TYPE_LABELS,
  getAmendmentCycleLabel,
  getAmendmentCycleShortLabel,
  USER_ROLE,
  ROLE_LABELS,
  MEETING_TYPE,
  MEETING_OUTCOME,
  BLOCKER_TYPE,
  BLOCKER_TYPE_LABELS,
  QUOTATION_ITEM_STATUS,
  QUOTATION_CATEGORY,
  API_ENDPOINTS,
  DATE_FORMAT,
  CURRENCY,
  FILE_SIZE_LIMITS,
  PAGINATION,
  NOTIFICATION_TYPE,
  // Site Database
  SITE_STATUS,
  SITE_STATUS_LABELS,
  SITE_STATUS_COLORS,
  SITE_PHASE_STATUS,
  SITE_PHASE_STATUS_LABELS,
  SITE_PHASE_STATUS_COLORS,
  SITE_TASK_STATUS,
  SITE_TASK_STATUS_LABELS,
  SITE_PROJECT_TYPE,
  SITE_PROJECT_TYPE_LABELS,
  SITE_PHASE_KEYS,
  SITE_HISTORY_CHANGE_TYPE,
  SITE_HISTORY_CHANGE_TYPE_LABELS
};
